import {Injectable} from '@angular/core';
import {RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle} from '@angular/router';
import { currentContextVersion } from './tab-session';

@Injectable()
export class CustomReuseStrategy implements RouteReuseStrategy {

    handlers: {[key: string]: DetachedRouteHandle} = {};
    /** The organization context the cached screens belong to; they are dropped when the tab switches organization or signs out. */
    private contextVersion = currentContextVersion();

    constructor() {}

    private isSameContext(): boolean {
        return currentContextVersion() === this.contextVersion;
    }

    calcKey(route: ActivatedRouteSnapshot) {                    /* Introduced to get the parent root level route e.g:[dashboard/""] */
        const url = route.pathFromRoot.map(x => x.url.map(u => u.path).join('/')).join(';');

        if (!url.length) { return undefined; }
        return url;
    }

    /**
     * Determines if this route (and its subtree) should be detached to be reused later.
    */
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        if (!this.isSameContext()) {    // Cache clear if the organization changed
            return false;
        }
        if (!route.routeConfig || route.routeConfig.loadChildren) {     // if module then bypass
            return false;
        }

        return !!route.data && !!(route.data as any).shouldDetach; // true - reuse all routes
    }

    /**
     * Stores the detached route.
     */
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {

        if (!route.routeConfig || route.routeConfig.loadChildren) { return; }                     // if module then bypass
        if (route.routeConfig.data && route.routeConfig.data.shouldDetach) {
            const key = this.calcKey(route);
            if (key) { this.handlers[key] = handle; }
        }

    }

    /**
     * Determines if this route (and its subtree) should be reattached.
     */
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (route.data?.component === "LoginComponent") {           // When logout clear the cache
            this.handlers = {};
            return false;
        }

        if (!this.isSameContext()) {        // Cache clear if the organization changed
            this.handlers = {};
            this.contextVersion = currentContextVersion();
            return false;
        }

        if (!route.routeConfig || route.routeConfig.loadChildren) { return false; }           // if module then bypass

        if (route.routeConfig.data && route.routeConfig.data.shouldDetach) {
            const key = this.calcKey(route);
            if (key) {  return !!this.handlers[key]; }
        }

        return false;
    }

    /**
    * Retrieves the previously stored route.
     */
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {

        if (!route.routeConfig || route.routeConfig.loadChildren) { return null; }                // if module then bypass

        if (route.routeConfig.data && route.routeConfig.data.shouldDetach) {
            const key = this.calcKey(route);
            if (key) { return this.handlers[key] || null; }
        }
        return null;

    }

    /**
     * Determines if a route should be reused.
     */
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        return future.routeConfig === curr.routeConfig;
    }
}
